/**
 * Export a rendered report to a multi-sheet .xlsx, client-side.
 *
 * The walker reads the report's rendered tables (WYSIWYG) so it covers every
 * report type without per-report code. Numeric fidelity:
 *   - a cell's `data-value` attribute is used verbatim when present (exact value),
 *   - otherwise numeric-looking text ("$1,234.56", "(2.3)", "4.1%") is coerced to
 *     a real number so totals/sorting work in the spreadsheet,
 *   - everything else exports as text.
 * Annotations the renderers may add to refine output:
 *   - `data-export-sheet="Name"` on a container -> one named sheet per container,
 *   - `data-export-skip` on an element -> excluded from export,
 *   - `data-export-extra-row="text"` on a cell -> emits one extra row beneath,
 *     with `text` in that cell's column (APX's two-row-per-holding shape),
 *   - `data-export-rows="Name"` on a container -> one named sheet built from the
 *     `data-export-row` elements inside it, for a block the report draws as divs
 *     rather than a table (the Average Capital & return card).
 * Without annotations, each top-level <table> becomes its own sheet, named from
 * the nearest preceding heading.
 */

function coerceNumber(text) {
  let s = String(text == null ? '' : text).trim();
  if (!s) return null;
  s = s.replace(/%$/, '').trim();
  let neg = false;
  if (/^\(.*\)$/.test(s)) {
    neg = true;
    s = s.slice(1, -1).trim();
  }
  s = s.replace(/[$,\s]/g, '');
  if (/^-/.test(s)) {
    neg = true;
    s = s.slice(1);
  }
  if (!/^\d*\.?\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

function cellValue(cell) {
  const dv = cell.getAttribute('data-value');
  if (dv != null && dv !== '') {
    const n = Number(dv);
    return Number.isFinite(n) ? n : dv;
  }
  const text = (cell.innerText || cell.textContent || '').replace(/\s+/g, ' ').trim();
  const n = coerceNumber(text);
  return n != null ? n : text;
}

function tableToAoa(table) {
  const aoa = [];
  table.querySelectorAll('tr').forEach((tr) => {
    if (tr.closest('[data-export-skip]')) return;
    const row = [];
    // Export-only continuation rows: `data-export-extra-row="text"` on a cell
    // emits an ADDITIONAL spreadsheet row directly beneath this one, carrying
    // that text in the annotated cell's own column and blanks elsewhere. Lets a
    // one-row-per-record screen table export in APX's two-row shape (FI Holdings
    // puts "<coupon>% Due <maturity>" on its own row so those fields are
    // extractable) without changing what the report renders. Column-indexed off
    // the same colspan-expanded cursor as the data row, so the text always lands
    // under its own header even with spanned cells.
    const extras = [];
    tr.querySelectorAll('th,td').forEach((cell) => {
      if (cell.hasAttribute('data-export-skip')) return;
      const extra = cell.getAttribute('data-export-extra-row');
      if (extra != null && extra !== '') extras.push([row.length, extra]);
      row.push(cellValue(cell));
      const span = parseInt(cell.getAttribute('colspan') || '1', 10);
      for (let k = 1; k < span; k += 1) row.push('');
    });
    if (row.length) aoa.push(row);
    if (row.length && extras.length) {
      const cont = new Array(row.length).fill('');
      extras.forEach(([i, text]) => { cont[i] = text; });
      aoa.push(cont);
    }
  });
  return aoa;
}

/**
 * Read a label/value block (`data-export-rows`) into rows. Each `data-export-row`
 * element's ELEMENT CHILDREN are its cells - label span + value span - so the
 * sheet holds the figure in its own column, which is also why a figure's label
 * and value must be separate spans rather than one interpolated string. A row
 * with NO element children is a sentence (the coverage/unavailable notes, the
 * asterisk caveat) and exports as one cell: the caveats qualify the headline
 * return and must travel with it. That sentence reading is keyed on having no
 * children at all, never on having no SURVIVING cells - keyed on the latter, a
 * row whose cells were every one of them skipped would fall back to the row's
 * own text and export the very content `data-export-skip` removed.
 * Two structural contracts are enforced below rather than left to the markup:
 * rows do not nest, and a row does not wrap a real <table>.
 */
function rowsToAoa(container) {
  const aoa = [];
  container.querySelectorAll('[data-export-row]').forEach((rowEl) => {
    if (rowEl.closest('[data-export-skip]')) return;
    // Nested row: the OUTER row wins and flattens this one's text into a cell of
    // its own, so a flat descendant query cannot also emit it as its own line.
    if (rowEl.parentElement.closest('[data-export-row]')) return;
    // A table is the table walker's to emit, as its own sheet; flattening it
    // here would export the same figures twice, the second time as one cell.
    if (rowEl.querySelector('table')) return;
    const children = Array.from(rowEl.children);
    if (!children.length) {
      aoa.push([cellValue(rowEl)]);
      return;
    }
    const cells = children.filter((c) => !c.hasAttribute('data-export-skip'));
    if (cells.length) aoa.push(cells.map(cellValue));
  });
  return aoa;
}

const EXCEL_MAX_SHEET_NAME = 31; // Excel rejects a longer name outright
const ELLIPSIS = '...';
const MEMBER_SEP = ' - ';
// Three characters are held back so a dedupe suffix (" 2" .. " 99") fits inside
// Excel's cap without eating into the name.
const BUDGET_BEFORE_DEDUPE = EXCEL_MAX_SHEET_NAME - 3;
// What a namespaced tab keeps of the account label come what may, so the tab
// still says whose it is.
const MIN_LABEL = 8;

function cleanSheetName(name) {
  return String(name || '').replace(/[\\/?*[\]:]/g, ' ').replace(/\s+/g, ' ').trim();
}

function sanitizeSheetName(name, used) {
  const base = cleanSheetName(name).slice(0, BUDGET_BEFORE_DEDUPE) || 'Sheet';
  let candidate = base;
  let i = 2;
  while (used.has(candidate.toLowerCase())) {
    // The suffix displaces the tail of the base rather than being sliced off it:
    // slicing the whole pair reproduced the 2-digit name once the suffix reached
    // 3 digits, and the loop then never exited. Member count is user data.
    const suffix = ` ${i}`;
    candidate = base.slice(0, EXCEL_MAX_SHEET_NAME - suffix.length) + suffix;
    i += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

/**
 * Namespace one member's sheet as "<account> - <sheet>". The SHEET name is what
 * tells a member's tabs apart, so it is reserved first and the account label is
 * ellipsized into whatever budget is left (never below MIN_LABEL, so the tab
 * still says whose it is). A label that already fits is untouched. A sheet name
 * longer than the budget less MIN_LABEL is still cut by the caller's truncation;
 * it just no longer loses to the label.
 */
function memberSheetName(accountLabel, sheetName) {
  const sheet = cleanSheetName(sheetName);
  const label = cleanSheetName(accountLabel);
  const room = Math.max(MIN_LABEL, BUDGET_BEFORE_DEDUPE - MEMBER_SEP.length - sheet.length);
  const kept = label.length <= room
    ? label
    : `${label.slice(0, room - ELLIPSIS.length).trim()}${ELLIPSIS}`;
  return `${kept}${MEMBER_SEP}${sheet}`;
}

function nearestHeading(table) {
  let el = table;
  for (let hops = 0; hops < 6 && el; hops += 1) {
    let sib = el.previousElementSibling;
    while (sib) {
      const match = sib.matches && sib.matches('h1,h2,h3,h4,[data-export-title]')
        ? sib
        : sib.querySelector && sib.querySelector('h1,h2,h3,h4,[data-export-title]');
      const text = match && (match.innerText || match.textContent || '').trim();
      if (text) return text;
      sib = sib.previousElementSibling;
    }
    el = el.parentElement;
  }
  return '';
}

/**
 * Collect raw `{ name, aoa }` sheets from ONE report subtree (no name sanitization).
 * `data-export-sheet="Name"` containers each become one merged sheet; otherwise
 * every top-level <table> is its own sheet, named from the nearest heading. Hidden
 * tables (`display:none`) are still walked via textContent. A `data-export-rows`
 * block contributes its own sheet in either branch, but not in the same place:
 * where the sheets come from headings it is interleaved with the tables in
 * document order (so the tabs run in the order the report reads), and where the
 * report names its own `data-export-sheet` containers it is appended after them.
 */
function collectRawSheets(rootEl) {
  const raw = [];
  const addTables = (name, tables) => {
    const aoa = [];
    tables.forEach((t, i) => {
      if (i > 0) aoa.push([]);
      tableToAoa(t).forEach((r) => aoa.push(r));
    });
    if (aoa.length) raw.push({ name, aoa });
  };
  const addRows = (el) => {
    const aoa = rowsToAoa(el);
    if (aoa.length) raw.push({ name: el.getAttribute('data-export-rows'), aoa });
  };
  const sheetEls = rootEl.querySelectorAll('[data-export-sheet]');
  if (sheetEls.length) {
    sheetEls.forEach((el) => addTables(el.getAttribute('data-export-sheet'), Array.from(el.querySelectorAll('table'))));
    rootEl.querySelectorAll('[data-export-rows]').forEach(addRows);
  } else {
    let n = 0;
    rootEl.querySelectorAll('table,[data-export-rows]').forEach((el) => {
      if (el.hasAttribute('data-export-rows')) {
        addRows(el);
        return;
      }
      n += 1;
      addTables(nearestHeading(el) || `Table ${n}`, [el]);
    });
  }
  return raw;
}

/**
 * Walk a rendered report's tables into `[{ name, aoa }]` sheets. Pure DOM → data
 * (no XLSX), so the export's fidelity is unit-testable without writing a file.
 *
 * Member-aware: the continuous viewer wraps each group portfolio in a
 * `.report-member[data-export-sheet]` section. When more than one is present the
 * report is a stacked group — process each portfolio independently (so a report's
 * own inner data-export-sheet / nearest-heading logic is unchanged) and namespace
 * each sheet name by the portfolio so they don't collide. A single report (one or
 * no `.report-member`) is walked exactly as before, so its export is unchanged.
 */
export function reportTablesToSheets(rootEl) {
  if (!rootEl) return [];
  const used = new Set();
  const sheets = [];
  const members = Array.from(rootEl.querySelectorAll('.report-member'));

  if (members.length > 1) {
    members.forEach((section, mi) => {
      const label = section.getAttribute('data-export-sheet') || `Account ${mi + 1}`;
      collectRawSheets(section).forEach(({ name, aoa }) =>
        sheets.push({ name: sanitizeSheetName(memberSheetName(label, name), used), aoa })
      );
    });
  } else {
    const scope = members[0] || rootEl;
    collectRawSheets(scope).forEach(({ name, aoa }) =>
      sheets.push({ name: sanitizeSheetName(name, used), aoa })
    );
  }

  if (!sheets.length) {
    const lines = (rootEl.innerText || rootEl.textContent || '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => [l]);
    sheets.push({ name: 'Report', aoa: lines.length ? lines : [['(no data)']] });
  }

  return sheets;
}

export async function exportReportToXlsx(rootEl, reportName) {
  if (!rootEl) return;
  const sheets = reportTablesToSheets(rootEl);
  if (!sheets.length) return;
  // xlsx is large (~100kB gzip); load it on demand so it never weighs down the
  // initial page bundle.
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, aoa }) =>
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), name)
  );

  const fname = `${String(reportName || 'report')
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80) || 'report'}.xlsx`;
  XLSX.writeFile(wb, fname);
}
